import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRecipeVote, useVoteOnRecipe } from '@/hooks/useRecipeVotes';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface VoteButtonsProps {
  recipeId: string;
  upvoteCount: number;
  compact?: boolean;
}

export function VoteButtons({ recipeId, upvoteCount, compact = false }: VoteButtonsProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { data: userVote, isLoading } = useRecipeVote(recipeId);
  const voteMutation = useVoteOnRecipe();

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setIsAuthenticated(!!user);
    });
  }, []);

  const handleVote = (e: React.MouseEvent, voteType: 'upvote' | 'downvote') => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      return;
    }
    
    voteMutation.mutate({ recipeId, voteType });
  };

  const isUpvoted = userVote?.vote_type === 'upvote';
  const isDownvoted = userVote?.vote_type === 'downvote';

  if (compact) {
    return (
      <TooltipProvider>
        <div className="flex items-center gap-1" onClick={(e) => e.preventDefault()}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-7 px-2 gap-1',
                  isUpvoted && 'text-success bg-success/10 hover:bg-success/20'
                )}
                onClick={(e) => handleVote(e, 'upvote')}
                disabled={!isAuthenticated || isLoading || voteMutation.isPending}
                aria-label={`Upvote recipe, ${upvoteCount} votes`}
              >
                <ThumbsUp className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">{upvoteCount}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isAuthenticated ? 'This recipe works!' : 'Sign in to vote'}</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-7 px-2',
                  isDownvoted && 'text-destructive bg-destructive/10 hover:bg-destructive/20'
                )}
                onClick={(e) => handleVote(e, 'downvote')}
                disabled={!isAuthenticated || isLoading || voteMutation.isPending}
                aria-label="Report recipe as not working"
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isAuthenticated ? 'Report as not working' : 'Sign in to report'}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2" onClick={(e) => e.preventDefault()}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={isUpvoted ? 'default' : 'outline'}
              size="sm"
              className={cn(
                'gap-2',
                isUpvoted && 'bg-success hover:bg-success/90 text-success-foreground'
              )}
              onClick={(e) => handleVote(e, 'upvote')}
              disabled={!isAuthenticated || isLoading || voteMutation.isPending}
            >
              <ThumbsUp className="h-4 w-4" />
              <span>{upvoteCount}</span>
              <span className="hidden sm:inline">Working</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isAuthenticated ? 'Mark this recipe as working' : 'Sign in to vote'}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={isDownvoted ? 'destructive' : 'outline'}
              size="sm"
              className="gap-2"
              onClick={(e) => handleVote(e, 'downvote')}
              disabled={!isAuthenticated || isLoading || voteMutation.isPending}
            >
              <ThumbsDown className="h-4 w-4" />
              <span className="hidden sm:inline">Not Working</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isAuthenticated ? 'Report this recipe as not working' : 'Sign in to report'}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
